import { z } from 'zod';

import { ApiResponseError, assertOk, parseJson } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions<T> {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  schema?: z.ZodType<T>;
  tenantSlug?: string;
}

const toIsoOrNull = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
  }
  return null;
};

const toIsoString = (value: unknown): string => toIsoOrNull(value) ?? new Date().toISOString();

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
};

const toObject = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
};

async function requestJson<T>(
  path: string,
  { method = 'GET', body, signal, schema, tenantSlug }: RequestOptions<T>,
): Promise<T> {
  const apiFetch = await resolveApiFetch();
  const targetSchema = schema ?? (z.unknown() as z.ZodType<T>);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (tenantSlug) {
    headers['X-Tenant'] = tenantSlug;
  }

  const shouldStringify = body !== undefined && !(body instanceof FormData);
  if (shouldStringify) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    headers,
    body: shouldStringify ? JSON.stringify(body) : (body as BodyInit | undefined),
    signal,
  });

  if (response.status === 204) {
    await assertOk(response);
    const parsed = targetSchema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  await assertOk(response);
  return parseJson(response, targetSchema);
}

export type DiscountApprovalStatus =
  | 'pending'
  | 'in_review'
  | 'approved'
  | 'declined'
  | 'escalated'
  | 'cancelled'
  | 'expired';

export type DiscountApprovalStepStatus = 'pending' | 'waiting' | 'approved' | 'skipped' | 'declined';

export interface DiscountApprovalListEntry {
  id: string;
  orderId: string;
  status: DiscountApprovalStatus;
  discountAmountCents: number;
  discountPercentBps: number | null;
  requestedAt: string;
  updatedAt: string;
  workflowId: string | null;
  policyId: string | null;
  reasonCode: string | null;
  currentStepOrder: number | null;
  currentApproverRole: string | null;
}

export interface DiscountApprovalStep {
  id: string;
  requestId: string;
  stepOrder: number;
  approverRole: string;
  approverId: string | null;
  status: DiscountApprovalStepStatus;
  respondedAt: string | null;
  responseNote: string | null;
  notifiedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountApprovalEvent {
  id: string;
  requestId: string;
  eventType: string;
  actorId: string | null;
  actorRole: string | null;
  occurredAt: string;
  createdAt: string;
  payload: Record<string, unknown>;
}

export interface DiscountApprovalReasonCode {
  code: string;
  label: string;
  requiresNote: boolean;
}

export interface DiscountApprovalPolicySummary {
  id: string;
  code: string;
  name: string;
  discountType: 'percentage' | 'amount';
  valueBps: number | null;
  valueCents: number | null;
  allowStacking: boolean;
  approvalRequired: boolean;
  maxStack: number | null;
  stackGroup: string | null;
  reasonCodes: DiscountApprovalReasonCode[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountApprovalWorkflowStep {
  id: string;
  stepOrder: number;
  approverRole: string;
  fallbackRole: string | null;
  notifyChannel: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountApprovalWorkflowSummary {
  id: string;
  name: string;
  thresholdCents: number;
  approverRoles: string[];
  escalationEmails: string[];
  requireReasonCode: boolean;
  steps: DiscountApprovalWorkflowStep[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountApprovalRequest {
  id: string;
  orderId: string;
  orderItemId: string | null;
  workflowId: string | null;
  policyId: string | null;
  reasonCode: string | null;
  reasonNote: string | null;
  requestedBy: string | null;
  status: DiscountApprovalStatus;
  approvalRequired: boolean;
  discountAmountCents: number;
  discountPercentBps: number | null;
  subtotalSnapshotCents: number | null;
  metadata: Record<string, unknown>;
  requestedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  expiresAt: string | null;
  lastNotifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountApprovalRequestDetail {
  request: DiscountApprovalRequest;
  steps: DiscountApprovalStep[];
  events: DiscountApprovalEvent[];
  policy: DiscountApprovalPolicySummary | null;
  workflow: DiscountApprovalWorkflowSummary | null;
}

const normalizeReasonCodes = (input: unknown): DiscountApprovalReasonCode[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const record = item as Record<string, unknown>;
      const code = toStringOrNull(record.code);
      const label = toStringOrNull(record.label);
      if (!code || !label) {
        return null;
      }
      return {
        code,
        label,
        requiresNote: Boolean(record.requiresNote),
      };
    })
    .filter((value): value is DiscountApprovalReasonCode => value !== null);
};

const normalizePolicy = (raw: unknown): DiscountApprovalPolicySummary | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const name = toStringOrNull(record.name);
  const code = toStringOrNull(record.code);
  const typeCandidate = toStringOrNull(record.discountType);
  if (!id || !name || !code || !typeCandidate) {
    return null;
  }
  const discountType = typeCandidate === 'amount' ? 'amount' : 'percentage';
  return {
    id,
    code,
    name,
    discountType,
    valueBps: record.valueBps === null || record.valueBps === undefined ? null : Math.round(Number(record.valueBps) || 0),
    valueCents:
      record.valueCents === null || record.valueCents === undefined
        ? null
        : Math.round(Number(record.valueCents) || 0),
    allowStacking: Boolean(record.allowStacking),
    approvalRequired: Boolean(record.approvalRequired),
    maxStack:
      record.maxStack === null || record.maxStack === undefined
        ? null
        : Math.max(1, Math.trunc(Number(record.maxStack) || 0)),
    stackGroup: toStringOrNull(record.stackGroup),
    reasonCodes: normalizeReasonCodes(record.reasonCodes),
    metadata: toObject(record.metadata),
    createdAt: toIsoString(record.createdAt),
    updatedAt: toIsoString(record.updatedAt),
  };
};

const normalizeWorkflowStep = (raw: unknown): DiscountApprovalWorkflowStep | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const role = toStringOrNull(record.approverRole);
  if (!id || !role) {
    return null;
  }
  const order = toNumber(record.stepOrder, 0);
  return {
    id,
    stepOrder: order,
    approverRole: role,
    fallbackRole: toStringOrNull(record.fallbackRole),
    notifyChannel: toStringOrNull(record.notifyChannel),
    metadata: toObject(record.metadata),
    createdAt: toIsoString(record.createdAt),
    updatedAt: toIsoString(record.updatedAt),
  };
};

const normalizeWorkflow = (raw: unknown): DiscountApprovalWorkflowSummary | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const name = toStringOrNull(record.name);
  if (!id || !name) {
    return null;
  }
  const approverRoles = Array.isArray(record.approverRoles)
    ? record.approverRoles
        .map((role) => toStringOrNull(role))
        .filter((role): role is string => Boolean(role))
    : [];
  const emails = Array.isArray(record.escalationEmails)
    ? record.escalationEmails
        .map((email) => toStringOrNull(email))
        .filter((email): email is string => Boolean(email))
    : [];
  const steps = Array.isArray(record.steps)
    ? record.steps
        .map((step) => normalizeWorkflowStep(step))
        .filter((step): step is DiscountApprovalWorkflowStep => step !== null)
        .sort((a, b) => a.stepOrder - b.stepOrder)
    : [];
  return {
    id,
    name,
    thresholdCents: Math.max(0, Math.trunc(Number(record.thresholdCents ?? 0) || 0)),
    approverRoles,
    escalationEmails: emails,
    requireReasonCode: record.requireReasonCode !== undefined ? Boolean(record.requireReasonCode) : true,
    steps,
    metadata: toObject(record.metadata),
    createdAt: toIsoString(record.createdAt),
    updatedAt: toIsoString(record.updatedAt),
  };
};

const normalizeApprovalStep = (raw: unknown): DiscountApprovalStep | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const requestId = toStringOrNull(record.requestId);
  const role = toStringOrNull(record.approverRole);
  if (!id || !requestId || !role) {
    return null;
  }
  const statusCandidate = toStringOrNull(record.status);
  const status: DiscountApprovalStepStatus =
    statusCandidate === 'approved' ||
    statusCandidate === 'skipped' ||
    statusCandidate === 'declined' ||
    statusCandidate === 'waiting'
      ? statusCandidate
      : 'pending';
  return {
    id,
    requestId,
    stepOrder: Math.trunc(Number(record.stepOrder ?? 0) || 0),
    approverRole: role,
    approverId: toStringOrNull(record.approverId),
    status,
    respondedAt: toIsoOrNull(record.respondedAt),
    responseNote: toStringOrNull(record.responseNote),
    notifiedAt: toIsoOrNull(record.notifiedAt),
    metadata: toObject(record.metadata),
    createdAt: toIsoString(record.createdAt),
    updatedAt: toIsoString(record.updatedAt),
  };
};

const normalizeApprovalEvent = (raw: unknown): DiscountApprovalEvent | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const requestId = toStringOrNull(record.requestId);
  const eventType = toStringOrNull(record.eventType);
  if (!id || !requestId || !eventType) {
    return null;
  }
  return {
    id,
    requestId,
    eventType,
    actorId: toStringOrNull(record.actorId),
    actorRole: toStringOrNull(record.actorRole),
    occurredAt: toIsoString(record.occurredAt),
    createdAt: toIsoString(record.createdAt),
    payload: toObject(record.payload),
  };
};

const normalizeApprovalRequest = (raw: unknown): DiscountApprovalRequest | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const orderId = toStringOrNull(record.orderId);
  if (!id || !orderId) {
    return null;
  }
  const statusCandidate = toStringOrNull(record.status);
  const status: DiscountApprovalStatus =
    statusCandidate === 'in_review' ||
    statusCandidate === 'approved' ||
    statusCandidate === 'declined' ||
    statusCandidate === 'escalated' ||
    statusCandidate === 'cancelled' ||
    statusCandidate === 'expired'
      ? statusCandidate
      : 'pending';
  return {
    id,
    orderId,
    orderItemId: toStringOrNull(record.orderItemId),
    workflowId: toStringOrNull(record.workflowId),
    policyId: toStringOrNull(record.policyId),
    reasonCode: toStringOrNull(record.reasonCode),
    reasonNote: toStringOrNull(record.reasonNote),
    requestedBy: toStringOrNull(record.requestedBy),
    status,
    approvalRequired: record.approvalRequired !== undefined ? Boolean(record.approvalRequired) : true,
    discountAmountCents: Math.round(Number(record.discountAmountCents ?? 0) || 0),
    discountPercentBps:
      record.discountPercentBps === null || record.discountPercentBps === undefined
        ? null
        : Math.round(Number(record.discountPercentBps) || 0),
    subtotalSnapshotCents:
      record.subtotalSnapshotCents === null || record.subtotalSnapshotCents === undefined
        ? null
        : Math.round(Number(record.subtotalSnapshotCents) || 0),
    metadata: toObject(record.metadata),
    requestedAt: toIsoString(record.requestedAt),
    resolvedAt: toIsoOrNull(record.resolvedAt),
    resolvedBy: toStringOrNull(record.resolvedBy),
    expiresAt: toIsoOrNull(record.expiresAt),
    lastNotifiedAt: toIsoOrNull(record.lastNotifiedAt),
    createdAt: toIsoString(record.createdAt),
    updatedAt: toIsoString(record.updatedAt),
  };
};

const normalizeListEntry = (raw: unknown): DiscountApprovalListEntry | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = toStringOrNull(record.id);
  const orderId = toStringOrNull(record.orderId);
  if (!id || !orderId) {
    return null;
  }
  const statusCandidate = toStringOrNull(record.status);
  const status: DiscountApprovalStatus =
    statusCandidate === 'in_review' ||
    statusCandidate === 'approved' ||
    statusCandidate === 'declined' ||
    statusCandidate === 'escalated' ||
    statusCandidate === 'cancelled' ||
    statusCandidate === 'expired'
      ? statusCandidate
      : 'pending';
  return {
    id,
    orderId,
    status,
    discountAmountCents: Math.round(Number(record.discountAmountCents ?? 0) || 0),
    discountPercentBps:
      record.discountPercentBps === null || record.discountPercentBps === undefined
        ? null
        : Math.round(Number(record.discountPercentBps) || 0),
    requestedAt: toIsoString(record.requestedAt),
    updatedAt: toIsoString(record.updatedAt),
    workflowId: toStringOrNull(record.workflowId),
    policyId: toStringOrNull(record.policyId),
    reasonCode: toStringOrNull(record.reasonCode),
    currentStepOrder:
      record.currentStepOrder === null || record.currentStepOrder === undefined
        ? null
        : Math.trunc(Number(record.currentStepOrder) || 0),
    currentApproverRole: toStringOrNull(record.currentApproverRole),
  };
};

const normalizeDetail = (detail: {
  request?: unknown;
  steps?: unknown[];
  events?: unknown[];
  policy?: unknown;
  workflow?: unknown;
}): DiscountApprovalRequestDetail => {
  const request = normalizeApprovalRequest(detail.request);
  if (!request) {
    throw new Error('Discount approval request malformed');
  }

  const steps = Array.isArray(detail.steps)
    ? detail.steps
        .map((step) => normalizeApprovalStep(step))
        .filter((step): step is DiscountApprovalStep => step !== null)
    : [];

  const events = Array.isArray(detail.events)
    ? detail.events
        .map((event) => normalizeApprovalEvent(event))
        .filter((event): event is DiscountApprovalEvent => event !== null)
    : [];

  return {
    request,
    steps,
    events,
    policy: normalizePolicy(detail.policy),
    workflow: normalizeWorkflow(detail.workflow),
  };
};

export interface ListDiscountApprovalsParams {
  status?: string;
  orderId?: string;
  limit?: number;
  signal?: AbortSignal;
}

const ListDiscountApprovalsResponseSchema = z
  .object({
    ok: z.boolean().optional(),
    requests: z.array(z.unknown()).optional(),
  })
  .passthrough();

const DiscountApprovalDetailSchema = z
  .object({
    request: z.unknown().optional(),
    steps: z.array(z.unknown()).optional(),
    events: z.array(z.unknown()).optional(),
    policy: z.unknown().optional(),
    workflow: z.unknown().optional(),
  })
  .passthrough();

const DiscountApprovalDetailEnvelopeSchema = z
  .object({
    ok: z.boolean().optional(),
    detail: DiscountApprovalDetailSchema.optional(),
  })
  .passthrough();

export const listDiscountApprovals = async (
  params: ListDiscountApprovalsParams = {},
): Promise<DiscountApprovalListEntry[]> => {
  const query = new URLSearchParams();
  if (params.status) {
    query.set('status', params.status);
  }
  if (params.orderId) {
    query.set('orderId', params.orderId);
  }
  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    query.set('limit', Math.max(1, Math.trunc(params.limit)).toString());
  }
  const suffix = query.toString();
  const response = await requestJson(`/discount-approvals${suffix ? `?${suffix}` : ''}`, {
    signal: params.signal,
    schema: ListDiscountApprovalsResponseSchema,
  });

  const list = Array.isArray(response.requests) ? response.requests : [];
  return list
    .map((entry) => normalizeListEntry(entry))
    .filter((entry): entry is DiscountApprovalListEntry => entry !== null);
};

export const getDiscountApprovalDetail = async (
  requestId: string,
  signal?: AbortSignal,
): Promise<DiscountApprovalRequestDetail> => {
  const payload = await requestJson(`/discount-approvals/${encodeURIComponent(requestId)}`, {
    signal,
    schema: DiscountApprovalDetailEnvelopeSchema,
  });

  if (!payload.detail) {
    throw new Error('Discount approval detail unavailable');
  }

  return normalizeDetail(payload.detail);
};

export interface SubmitDiscountApprovalPayload {
  note?: string;
}

export const submitDiscountApproval = async (
  requestId: string,
  payload: SubmitDiscountApprovalPayload = {},
): Promise<DiscountApprovalRequestDetail> => {
  const response = await requestJson(`/discount-approvals/${encodeURIComponent(requestId)}/submit`, {
    method: 'POST',
    body: payload,
    schema: DiscountApprovalDetailEnvelopeSchema,
  });

  if (!response.detail) {
    return getDiscountApprovalDetail(requestId);
  }

  return normalizeDetail(response.detail);
};

export interface ApplyDiscountApprovalDecisionPayload {
  decision: 'approve' | 'decline';
  note?: string;
}

export const applyDiscountApprovalDecision = async (
  requestId: string,
  stepId: string,
  payload: ApplyDiscountApprovalDecisionPayload,
): Promise<DiscountApprovalRequestDetail> => {
  const response = await requestJson(
    `/discount-approvals/${encodeURIComponent(requestId)}/steps/${encodeURIComponent(stepId)}/decision`,
    {
      method: 'POST',
      body: payload,
      schema: DiscountApprovalDetailEnvelopeSchema,
    },
  );

  if (!response.detail) {
    return getDiscountApprovalDetail(requestId);
  }

  return normalizeDetail(response.detail);
};

export interface CancelDiscountApprovalPayload {
  reason?: string;
}

export const cancelDiscountApproval = async (
  requestId: string,
  payload: CancelDiscountApprovalPayload = {},
): Promise<DiscountApprovalRequestDetail> => {
  const response = await requestJson(`/discount-approvals/${encodeURIComponent(requestId)}/cancel`, {
    method: 'POST',
    body: payload,
    schema: DiscountApprovalDetailEnvelopeSchema,
  });

  if (!response.detail) {
    return getDiscountApprovalDetail(requestId);
  }

  return normalizeDetail(response.detail);
};
